import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { BookOpen, User, Calendar, AlertTriangle, Clock, ClipboardList } from 'lucide-react';
import PageWrapper from '../components/PageWrapper';
import Breadcrumbs from '../components/Breadcrumbs';
import MedicalText from '../components/MedicalText';
import RelatedVideos from '../components/RelatedVideos';
import LanguageToggle from '../components/LanguageToggle';
import { calculateReadingTime } from '../data/educationCategories';
import { t, translateHeading } from '../utils/translations';
import { findAfterVisitForArticle } from '../data/procedureAfterVisitLinks';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Spanish translations for markdown content
const translateMarkdown = (content, language) => {
  if (language === 'en' || !content) return content;
  
  let translated = content;
  
  // Translate common headings
  const headingPatterns = [
    [/^# Overview/gm, '# Descripción General'],
    [/^## Overview/gm, '## Descripción General'],
    [/^# What is/gm, '# Qué es'],
    [/^## What is/gm, '## Qué es'],
    [/^# Symptoms/gm, '# Síntomas'],
    [/^## Symptoms/gm, '## Síntomas'],
    [/^### Symptoms/gm, '### Síntomas'],
    [/^# Causes/gm, '# Causas'],
    [/^## Causes/gm, '## Causas'],
    [/^## Common Causes/gm, '## Causas Comunes'],
    [/^# Risk Factors/gm, '# Factores de Riesgo'],
    [/^## Risk Factors/gm, '## Factores de Riesgo'],
    [/^# Diagnosis/gm, '# Diagnóstico'],
    [/^## Diagnosis/gm, '## Diagnóstico'],
    [/^## Diagnostic/gm, '## Diagnóstico'],
    [/^# Treatment/gm, '# Tratamiento'],
    [/^## Treatment/gm, '## Tratamiento'],
    [/^## Treatment Options/gm, '## Opciones de Tratamiento'],
    [/^# Prevention/gm, '# Prevención'],
    [/^## Prevention/gm, '## Prevención'],
    [/^# Recovery/gm, '# Recuperación'],
    [/^## Recovery/gm, '## Recuperación'],
    [/^## Recovery Timeline/gm, '## Cronograma de Recuperación'],
    [/^# Prognosis/gm, '# Pronóstico'],
    [/^## Prognosis/gm, '## Pronóstico'],
    [/^# Complications/gm, '# Complicaciones'],
    [/^## Complications/gm, '## Complicaciones'],
    [/^## Potential Complications/gm, '## Posibles Complicaciones'],
    [/^# When to See/gm, '# Cuándo Ver'],
    [/^## When to See/gm, '## Cuándo Ver'],
    [/^## When to Seek/gm, '## Cuándo Buscar'],
    [/^## When to Call/gm, '## Cuándo Llamar'],
    [/^# Key Takeaways/gm, '# Puntos Clave'],
    [/^## Key Takeaways/gm, '## Puntos Clave'],
    [/^# Summary/gm, '# Resumen'],
    [/^## Summary/gm, '## Resumen'],
    [/^# Before the Procedure/gm, '# Antes del Procedimiento'],
    [/^## Before the Procedure/gm, '## Antes del Procedimiento'],
    [/^## Before Surgery/gm, '## Antes de la Cirugía'],
    [/^# After the Procedure/gm, '# Después del Procedimiento'],
    [/^## After the Procedure/gm, '## Después del Procedimiento'],
    [/^## After Surgery/gm, '## Después de la Cirugía'],
    [/^# The Procedure/gm, '# El Procedimiento'],
    [/^## The Procedure/gm, '## El Procedimiento'],
    [/^## The Surgery/gm, '## La Cirugía'],
    [/^# What to Expect/gm, '# Qué Esperar'],
    [/^## What to Expect/gm, '## Qué Esperar'],
    [/^# How It Works/gm, '# Cómo Funciona'],
    [/^## How It Works/gm, '## Cómo Funciona'],
    [/^# Benefits/gm, '# Beneficios'],
    [/^## Benefits/gm, '## Beneficios'],
    [/^## Advantages/gm, '## Ventajas'],
    [/^# Risks/gm, '# Riesgos'],
    [/^## Risks/gm, '## Riesgos'],
    [/^# Side Effects/gm, '# Efectos Secundarios'],
    [/^## Side Effects/gm, '## Efectos Secundarios'],
    [/^# Follow-Up/gm, '# Seguimiento'],
    [/^## Follow-Up/gm, '## Seguimiento'],
    [/^## Follow-Up Care/gm, '## Cuidado de Seguimiento'],
    [/^# Medications/gm, '# Medicamentos'],
    [/^## Medications/gm, '## Medicamentos'],
    [/^# Surgery/gm, '# Cirugía'],
    [/^## Surgery/gm, '## Cirugía'],
    [/^## Surgical Options/gm, '## Opciones Quirúrgicas'],
    [/^# Types/gm, '# Tipos'],
    [/^## Types/gm, '## Tipos'],
    [/^# Stages/gm, '# Etapas'],
    [/^## Stages/gm, '## Etapas'],
    [/^## Staging/gm, '## Estadificación'],
    [/^# Understanding/gm, '# Entendiendo'],
    [/^## Understanding/gm, '## Entendiendo'],
    [/^## Important/gm, '## Importante'],
    [/^## Contact/gm, '## Contacte'],
    [/^## Activity/gm, '## Actividad'],
    [/^## Activity Guidelines/gm, '## Guías de Actividad'],
    [/^## Lifestyle/gm, '## Estilo de Vida'],
    [/^## Self-Help/gm, '## Autoayuda'],
    [/^## Tips/gm, '## Consejos'],
    [/^## Warning/gm, '## Advertencia'],
    [/^## Normal/gm, '## Normal'],
    [/^## Anesthesia/gm, '## Anestesia'],
    [/^## Hospital Stay/gm, '## Estancia Hospitalaria'],
    [/^## Going Home/gm, '## Regreso a Casa'],
    [/^## First/gm, '## Primer'],
    [/^## Week/gm, '## Semana'],
    [/^## Living with/gm, '## Viviendo con'],
    [/^## Managing/gm, '## Manejando'],
    [/^## Success Rates/gm, '## Tasas de Éxito'],
    [/^## Comparison/gm, '## Comparación'],
    [/^## Questions/gm, '## Preguntas'],
    [/^---$/gm, '---'],
  ];
  
  headingPatterns.forEach(([pattern, replacement]) => {
    translated = translated.replace(pattern, replacement);
  });
  
  // Translate common phrases
  const phrasePatterns = [
    [/\*This information is for educational purposes only/g, '*Esta información es solo para fines educativos'],
    [/Always consult your healthcare provider/g, 'Siempre consulte a su proveedor de atención médica'],
    [/for personalized guidance\.\*/g, 'para orientación personalizada.*'],
    [/Call your doctor if/gi, 'Llame a su médico si'],
    [/Seek immediate care/gi, 'Busque atención inmediata'],
    [/Contact your urologist/gi, 'Contacte a su urólogo'],
    [/Go to the emergency room/gi, 'Vaya a la sala de emergencias'],
  ];
  
  phrasePatterns.forEach(([pattern, replacement]) => {
    translated = translated.replace(pattern, replacement);
  });
  
  return translated;
};

const ArticlePage = () => {
  const { articleId } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [language, setLanguage] = useState('en');

  // Scroll to top when article loads
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [articleId]);

  useEffect(() => {
    const loadArticle = async () => {
      try {
        const response = await axios.get(`${API}/blog/${articleId}`);
        setArticle(response.data);
      } catch (error) {
        console.error("Error loading article:", error);
      } finally {
        setLoading(false);
      }
    };
    loadArticle();
  }, [articleId]);

  const toggleLanguage = () => {
    setLanguage(lang => lang === 'en' ? 'es' : 'en');
  };

  if (loading) {
    return (
      <PageWrapper>
        <div className="loading-state" data-testid="loading-state">
          <div className="spinner"></div>
          <p>{t('loading', language)}</p>
        </div>
      </PageWrapper>
    );
  }

  if (!article) {
    return (
      <PageWrapper>
        <div className="empty-state" data-testid="article-not-found">
          <BookOpen size={48} />
          <h2>{language === 'en' ? 'Article Not Found' : 'Artículo No Encontrado'}</h2>
          <button onClick={() => navigate('/education')} className="btn-primary" data-testid="back-to-learn-btn">
            {t('backToLibrary', language)}
          </button>
        </div>
      </PageWrapper>
    );
  }

  const readingTime = calculateReadingTime(article.content);
  
  // Find related after-visit care guide
  const relatedAfterVisit = findAfterVisitForArticle(article.title);
  
  // Use Spanish content from database if available, otherwise fall back to English
  const displayContent = language === 'es' && article.content_es 
    ? article.content_es 
    : translateMarkdown(article.content, language);

  // Generate breadcrumbs based on article category
  const breadcrumbItems = [
    { label: language === 'en' ? 'Education Library' : 'Biblioteca Educativa', path: '/education' },
    { label: article.category || (language === 'en' ? 'Articles' : 'Artículos'), path: `/education?category=${article.category?.toLowerCase().replace(/\s+/g, '-') || 'all'}` },
    { label: article.title, path: `/learn/${articleId}` }
  ];

  return (
    <PageWrapper>
      <Breadcrumbs items={breadcrumbItems} />

      <article className="article-full" data-testid="article-full">
        <header className="article-header">
          <div className="article-header-top">
            <h1>{article.title}</h1>
            <LanguageToggle language={language} onToggle={toggleLanguage} />
          </div>
          <div className="article-meta">
            <span><User size={16} /> {article.author}</span>
            <span><Calendar size={16} /> {new Date(article.created_at).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US')}</span>
            <span><Clock size={16} /> {readingTime} {t('minRead', language)}</span>
          </div>
        </header>

        <div className="article-body">
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
              // Render images properly
              img: ({ src, alt }) => (
                <figure className="article-image">
                  <img src={src} alt={alt} loading="lazy" />
                </figure>
              ),
              // Style image captions (emphasis after images)
              em: ({ children }) => <em className="image-caption">{children}</em>,
              // Wrap text nodes with MedicalText for auto-tooltips
              p: ({ children }) => <p><MedicalText>{typeof children === 'string' ? children : children}</MedicalText></p>,
              li: ({ children }) => <li><MedicalText>{typeof children === 'string' ? children : children}</MedicalText></li>,
              // Keep table elements as-is for proper rendering
              table: ({ children }) => <table>{children}</table>,
              thead: ({ children }) => <thead>{children}</thead>,
              tbody: ({ children }) => <tbody>{children}</tbody>,
              tr: ({ children }) => <tr>{children}</tr>,
              th: ({ children }) => <th>{children}</th>,
              td: ({ children }) => <td><MedicalText>{children}</MedicalText></td>,
            }}
          >
            {displayContent}
          </ReactMarkdown>
        </div>

        {/* When to Call Your Doctor */}
        <div className="warning-box" data-testid="warning-box">
          <AlertTriangle size={20} />
          <div>
            <strong>{t('whenToCallDoctorImmediately', language)}</strong>
            <p>{t('seekImmediateCare', language)}</p>
          </div>
        </div>

        {/* Related After-Visit Care Guide */}
        {relatedAfterVisit && (
          <div className="related-aftercare-box" data-testid="related-aftercare-box">
            <ClipboardList size={20} />
            <div>
              <strong>{language === 'en' ? 'After Your Procedure' : 'Después de Su Procedimiento'}</strong>
              <p>
                {language === 'en' 
                  ? 'Had this procedure? View your post-procedure care instructions:'
                  : '¿Se realizó este procedimiento? Vea las instrucciones de cuidado:'}
              </p>
              <Link to={`/after-visit/${relatedAfterVisit.afterVisitId}`} className="aftercare-link">
                {relatedAfterVisit.afterVisitTitle} →
              </Link>
            </div>
          </div>
        )}

        {/* Disclaimer */}
        <div className="article-disclaimer" data-testid="article-disclaimer">
          <AlertTriangle size={16} />
          <p><strong>{language === 'en' ? 'Important:' : 'Importante:'}</strong> {t('disclaimerText', language)}</p>
        </div>

        {/* Related Videos */}
        <RelatedVideos 
          articleCategory={article.category} 
          articleTitle={article.title}
          language={language}
        />
      </article>
    </PageWrapper>
  );
};

export default ArticlePage;
